import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PageLayout from 'layouts/PageLayout';
import ModalBusyContainer from 'components/ModalBusyContainer';
import { actions as modalActions } from 'redux/modal';
import { getConfirmationModalConfig } from 'utils';
import { routesAliases } from 'router/routesAliases';
import {
  applySafeLanguageManagerChanges,
  attachLanguageManagerCourse,
  getLanguageManagerInventory,
  refreshLanguageManager,
  replaceLanguageManagerCourse,
} from 'http/moodle';
import {
  apiParamsForQuery,
  countSelectedSafeChanges,
  parseLanguageManagerQuery,
  scopeForSelection,
  serializeLanguageManagerQuery,
} from './helpers';

const STATUS_OPTIONS = [
  ['up_to_date', 'Up to date'],
  ['new_languages', 'New languages available'],
  ['warning', 'Warning'],
  ['action_required', 'Action required'],
  ['not_refreshed', 'Not refreshed'],
];
const TYPE_OPTIONS = [['standard', 'Standard']];
const REFRESH_OPTIONS = [
  ['never', 'Never refreshed'],
  ['fresh', 'Fresh'],
  ['stale', 'Stale'],
];
const STATUS_COLORS = {
  up_to_date: 'default',
  new_languages: 'primary',
  warning: 'secondary',
  action_required: 'secondary',
  not_refreshed: 'default',
};
const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS);
const MOODLE_BASE_URL = String(process.env.REACT_APP_MOODLE_BASE_URL || '').replace(/\/$/, '');

const useStyles = makeStyles(theme => ({
  summary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(150px, 1fr))',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  summaryButton: {
    justifyContent: 'space-between',
    textTransform: 'none',
    padding: theme.spacing(1.5),
  },
  toolbar: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  search: { minWidth: 320 },
  filter: { minWidth: 210 },
  tableWrapper: { overflowX: 'auto' },
  table: { minWidth: 1450 },
  compactTable: { minWidth: 1200 },
  chip: { margin: theme.spacing(0.25) },
  availableChip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  warningChip: {
    backgroundColor: theme.palette.warning ? theme.palette.warning.main : '#f9a825',
  },
  errorChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  expanded: { backgroundColor: theme.palette.grey[50] },
  section: { padding: theme.spacing(2) },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  bulkSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  resultList: { maxHeight: 360, overflowY: 'auto' },
}));

const MultiFilter = ({ label, value, options, onChange }) => (
  <FormControl variant="outlined" size="small" style={{ minWidth: 210 }}>
    <InputLabel>{label}</InputLabel>
    <Select
      multiple
      value={value}
      onChange={event => onChange(event.target.value)}
      label={label}
      renderValue={selected =>
        selected.map(item => (options.find(option => option[0] === item) || [item, item])[1]).join(', ')
      }
    >
      {options.map(([optionValue, optionLabel]) => (
        <MenuItem key={optionValue} value={optionValue}>
          <Checkbox checked={value.includes(optionValue)} color="primary" />
          <ListItemText primary={optionLabel} />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const CourseIdentity = ({ course }) => {
  if (!course) return '-';
  const version =
    course.moduleCode && course.version && course.revision
      ? `${course.moduleCode}-${course.version}.${course.revision}-${course.language}`
      : course.shortname;
  return version || '-';
};

const ResultDialog = ({ result }) => (
  <Box>
    <Typography gutterBottom>
      Linked: {result.summary?.linked || 0}; refreshed: {result.summary?.refreshed || 0}; skipped:{' '}
      {result.summary?.skipped || 0}; failed: {result.summary?.failed || 0}.
    </Typography>
    <div>
      {(result.results || []).map(item => (
        <Typography key={item.trainingPartId} variant="body2">
          #{item.trainingPartId}: {item.status}
          {item.linked ? ` (${item.linked} linked)` : ''}
          {item.message ? ` — ${item.message}` : ''}
        </Typography>
      ))}
    </div>
  </Box>
);

const ReplacementDialog = ({ candidates, onCancel, onConfirm }) => {
  const [courseId, setCourseId] = useState(candidates[0]?.courseId || '');
  return (
    <Box>
      <FormControl variant="outlined" fullWidth>
        <InputLabel>Replacement Moodle course</InputLabel>
        <Select value={courseId} onChange={event => setCourseId(event.target.value)} label="Replacement Moodle course">
          {candidates.map(candidate => (
            <MenuItem key={candidate.courseId} value={candidate.courseId}>
              {candidate.courseName} — {candidate.shortname} (#{candidate.courseId})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Box ml={1}>
          <Button color="primary" variant="contained" disabled={!courseId} onClick={() => onConfirm(courseId)}>
            Replace
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const LanguageManager = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation('common');
  const query = useMemo(() => parseLanguageManagerQuery(location.search), [location.search]);
  const [inventory, setInventory] = useState({
    items: [],
    totalCount: 0,
    summary: {},
    filterOptions: { languages: [], versions: [], issues: [] },
  });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [expandedVersions, setExpandedVersions] = useState({});
  const [selected, setSelected] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [moreFilters, setMoreFilters] = useState(false);
  const [searchDraft, setSearchDraft] = useState(query.search);

  useEffect(() => setSearchDraft(query.search), [query.search]);

  const updateQuery = useCallback(
    changes => {
      const next = { ...query, ...changes };
      history.replace({
        pathname: location.pathname,
        search: serializeLanguageManagerQuery(next),
      });
    },
    [history, location.pathname, query],
  );

  const loadInventory = useCallback(async () => {
    setLoading(true);
    const response = await getLanguageManagerInventory(apiParamsForQuery(query));
    setLoading(false);
    if (response.error || response.statusCode) {
      toast.error(response.message || 'Could not load Language Manager.');
      return;
    }
    setInventory(response);
    setSelectedRows(previous => {
      const next = { ...previous };
      (response.items || []).forEach(row => {
        next[row.id] = row;
      });
      return next;
    });
  }, [query]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const runAndReload = async operation => {
    setLoading(true);
    const response = await operation();
    setLoading(false);
    if (response.error || response.statusCode) {
      toast.error(response.message || 'Language Manager operation failed.');
      return;
    }
    await loadInventory();
    dispatch(
      modalActions.openModal({ size: 'medium', title: 'Language Manager result' }, <ResultDialog result={response} />),
    );
  };

  const refreshSelected = () => runAndReload(() => refreshLanguageManager({ trainingPartIds: selected }));

  const refreshAllMatching = () =>
    runAndReload(() =>
      refreshLanguageManager({
        filters: apiParamsForQuery({ ...query, page: 0, pageSize: 100 }),
      }),
    );

  const safeChanges = selected.length
    ? countSelectedSafeChanges(selected, selectedRows)
    : inventory.summary?.safeChanges || 0;
  const affectedCourses = selected.length ? selected.length : inventory.summary?.coursesWithChanges || 0;
  const warningCount = selected.length
    ? selected.reduce((total, id) => total + (selectedRows[id]?.warningCount || 0), 0)
    : inventory.summary?.warnings || 0;

  const applyAll = () => {
    const scope = scopeForSelection(selected, query);
    const [config, element] = getConfirmationModalConfig({
      title: 'Apply safe language links',
      message: `Link ${safeChanges} language course(s) across ${affectedCourses} training part(s). ${warningCount} warning(s) are included. Existing links will not be changed.`,
      confirmTitle: t('buttons.save'),
      rejectTitle: t('buttons.cancel'),
      confirmAction: () => {
        dispatch(modalActions.closeModal());
        runAndReload(() => applySafeLanguageManagerChanges(scope));
      },
      rejectAction: () => dispatch(modalActions.closeModal()),
    });
    dispatch(modalActions.openModal(config, element));
  };

  const attach = (row, languageRow) => {
    const course = languageRow.course;
    const [config, element] = getConfirmationModalConfig({
      title: `Attach ${languageRow.language}`,
      message: `Attach ${course.shortname || course.courseName} (#${course.courseId}) to ${row.courseSystemId}?`,
      confirmTitle: t('buttons.save'),
      rejectTitle: t('buttons.cancel'),
      confirmAction: () => {
        dispatch(modalActions.closeModal());
        runAndReload(() => attachLanguageManagerCourse(row.id, languageRow.language, course.courseId));
      },
      rejectAction: () => dispatch(modalActions.closeModal()),
    });
    dispatch(modalActions.openModal(config, element));
  };

  const replace = (row, languageRow) => {
    dispatch(
      modalActions.openModal(
        { size: 'medium', title: `Replace ${row.courseSystemId}-${languageRow.language}` },
        <ReplacementDialog
          candidates={languageRow.replacementCandidates || []}
          onCancel={() => dispatch(modalActions.closeModal())}
          onConfirm={courseId => {
            dispatch(modalActions.closeModal());
            runAndReload(() => replaceLanguageManagerCourse(row.id, languageRow.language, courseId));
          }}
        />,
      ),
    );
  };

  const toggleSelection = row => {
    setSelected(previous => (previous.includes(row.id) ? previous.filter(id => id !== row.id) : [...previous, row.id]));
    setSelectedRows(previous => ({ ...previous, [row.id]: row }));
  };

  const currentPageIds = inventory.items.map(row => row.id);
  const allPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => selected.includes(id));
  const togglePage = () => {
    if (allPageSelected) {
      setSelected(previous => previous.filter(id => !currentPageIds.includes(id)));
    } else {
      setSelected(previous => Array.from(new Set([...previous, ...currentPageIds])));
      setSelectedRows(previous => ({
        ...previous,
        ...Object.fromEntries(inventory.items.map(row => [row.id, row])),
      }));
    }
  };

  const setFilter = (name, value) => updateQuery({ [name]: value, page: 0 });
  const sort = field => {
    const same = query.sortBy === field;
    updateQuery({ sortBy: field, sortDir: same && query.sortDir === 'asc' ? 'desc' : 'asc', page: 0 });
  };
  const sortableHeader = (label, field) => (
    <TableSortLabel active={query.sortBy === field} direction={query.sortDir} onClick={() => sort(field)}>
      {label}
    </TableSortLabel>
  );

  const getMoodleUrl = course =>
    course?.courseUrl ||
    (MOODLE_BASE_URL && course?.courseId ? `${MOODLE_BASE_URL}/course/view.php?id=${course.courseId}` : '');

  const openMoodle = course => {
    const url = getMoodleUrl(course);
    if (url) window.open(url, '_blank', 'noopener');
  };

  return (
    <PageLayout
      pageTitle="Language Manager"
      actionSectionRight={[
        {
          type: 'button',
          icon: 'refresh',
          title: `Refresh selected${selected.length ? ` (${selected.length})` : ''}`,
          disabled: !selected.length || loading,
          onClick: refreshSelected,
        },
        {
          type: 'button',
          icon: 'sync',
          title: 'Refresh all matching',
          disabled: loading || !inventory.totalCount,
          onClick: refreshAllMatching,
        },
        {
          type: 'button',
          icon: 'done_all',
          title: `Apply all safe changes${safeChanges ? ` (${safeChanges})` : ''}`,
          disabled: loading || !safeChanges,
          onClick: applyAll,
        },
      ]}
    >
      <ModalBusyContainer busy={loading}>
        <div className={classes.summary}>
          {STATUS_OPTIONS.map(([status, label]) => (
            <Button
              key={status}
              className={classes.summaryButton}
              variant={query.status.includes(status) ? 'contained' : 'outlined'}
              color={status === 'action_required' || status === 'warning' ? 'secondary' : 'primary'}
              onClick={() => setFilter('status', query.status.includes(status) ? [] : [status])}
            >
              <span>{label}</span>
              <strong>{inventory.summary?.[status] || 0}</strong>
            </Button>
          ))}
        </div>

        <Paper className={classes.toolbar}>
          <div className={classes.filterRow}>
            <TextField
              className={classes.search}
              variant="outlined"
              size="small"
              label="Search"
              placeholder="Code, training part, Moodle course, shortname or ID"
              value={searchDraft}
              onChange={event => setSearchDraft(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') updateQuery({ search: searchDraft.trim(), page: 0 });
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<Icon>search</Icon>}
              onClick={() => updateQuery({ search: searchDraft.trim(), page: 0 })}
            >
              Search
            </Button>
            <MultiFilter
              label="Status"
              value={query.status}
              options={STATUS_OPTIONS}
              onChange={value => setFilter('status', value)}
            />
            <MultiFilter
              label="Language"
              value={query.language}
              options={(inventory.filterOptions?.languages || []).map(value => [value, value])}
              onChange={value => setFilter('language', value)}
            />
            <Button
              startIcon={<Icon>{moreFilters ? 'expand_less' : 'expand_more'}</Icon>}
              onClick={() => setMoreFilters(!moreFilters)}
            >
              More filters
            </Button>
            <Button onClick={() => history.replace({ pathname: location.pathname, search: '' })}>Clear filters</Button>
          </div>
          <Collapse in={moreFilters}>
            <Box mt={2} className={classes.filterRow}>
              <MultiFilter
                label="Version"
                value={query.version}
                options={(inventory.filterOptions?.versions || []).map(value => [value, value])}
                onChange={value => setFilter('version', value)}
              />
              <MultiFilter
                label="Validation issue"
                value={query.issue}
                options={(inventory.filterOptions?.issues || []).map(value => [value, value.replace(/_/g, ' ')])}
                onChange={value => setFilter('issue', value)}
              />
              <MultiFilter
                label="Type"
                value={query.type}
                options={TYPE_OPTIONS}
                onChange={value => setFilter('type', value)}
              />
              <MultiFilter
                label="Refresh state"
                value={query.refreshState}
                options={REFRESH_OPTIONS}
                onChange={value => setFilter('refreshState', value)}
              />
            </Box>
          </Collapse>
        </Paper>

        <Paper className={classes.tableWrapper}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={allPageSelected} onChange={togglePage} color="primary" />
                </TableCell>
                <TableCell />
                <TableCell>{sortableHeader('Code', 'courseSystemId')}</TableCell>
                <TableCell>{sortableHeader('Training part', 'name')}</TableCell>
                <TableCell>{sortableHeader('Current version', 'version')}</TableCell>
                <TableCell>Languages</TableCell>
                <TableCell>Available</TableCell>
                <TableCell>Other versions</TableCell>
                <TableCell>{sortableHeader('Status', 'status')}</TableCell>
                <TableCell>{sortableHeader('Last refreshed', 'scannedAt')}</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.items.map(row => (
                <React.Fragment key={row.id}>
                  <TableRow hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(row.id)}
                        onChange={() => toggleSelection(row)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell padding="checkbox">
                      <IconButton
                        size="small"
                        onClick={() => setExpanded(previous => ({ ...previous, [row.id]: !previous[row.id] }))}
                      >
                        <Icon>{expanded[row.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                      </IconButton>
                    </TableCell>
                    <TableCell>{row.courseSystemId}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.currentVersion?.label || 'Unknown'}</TableCell>
                    <TableCell>
                      {row.attachedLanguages.map(language => (
                        <Chip key={language} className={classes.chip} size="small" label={language} />
                      ))}
                    </TableCell>
                    <TableCell>
                      {row.availableLanguages.map(language => (
                        <Chip
                          key={language}
                          className={`${classes.chip} ${classes.availableChip}`}
                          size="small"
                          label={language}
                        />
                      ))}
                    </TableCell>
                    <TableCell>{row.otherVersions.length}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={STATUS_COLORS[row.status]}
                        label={STATUS_LABELS[row.status] || row.status}
                      />
                    </TableCell>
                    <TableCell>
                      {row.scannedAt ? new Date(row.scannedAt).toLocaleString() : 'Never'}
                      <Typography variant="caption" display="block">
                        {row.refreshState}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Refresh course">
                        <IconButton
                          size="small"
                          onClick={() => runAndReload(() => refreshLanguageManager({ trainingPartIds: [row.id] }))}
                        >
                          <Icon>refresh</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit training part">
                        <IconButton
                          size="small"
                          onClick={() =>
                            history.push(
                              `${routesAliases.editTrainingPart}/${row.trainingModuleId}/training-parts/edit/${row.id}/${row.trainingMethod}`,
                            )
                          }
                        >
                          <Icon>edit</Icon>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.expanded}>
                    <TableCell colSpan={11} style={{ paddingBottom: 0, paddingTop: 0 }}>
                      <Collapse in={Boolean(expanded[row.id])} timeout="auto" unmountOnExit>
                        <div className={classes.section}>
                          <div className={classes.sectionHeader}>
                            <Typography variant="h6">
                              Current version: {row.currentVersion?.label || 'Unknown'}
                            </Typography>
                            {row.scanError && <Typography color="error">{row.scanError}</Typography>}
                          </div>
                          <Table className={classes.compactTable} size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Language</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell>Moodle course</TableCell>
                                <TableCell>IDs</TableCell>
                                <TableCell>Identity</TableCell>
                                <TableCell>Validation</TableCell>
                                <TableCell>Last validated</TableCell>
                                <TableCell align="center">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(row.currentVersion?.languages || []).map((languageRow, index) => (
                                <TableRow
                                  key={`${languageRow.language}-${languageRow.course?.courseId || index}`}
                                  selected={query.language.includes(languageRow.language)}
                                >
                                  <TableCell>{languageRow.language}</TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      className={
                                        languageRow.state === 'invalid'
                                          ? classes.errorChip
                                          : languageRow.state === 'warning'
                                          ? classes.warningChip
                                          : languageRow.state === 'available'
                                          ? classes.availableChip
                                          : ''
                                      }
                                      label={languageRow.state}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {languageRow.course?.courseName || 'Unresolved Moodle course'}
                                    <Typography variant="caption" display="block">
                                      {languageRow.course?.shortname}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    Course #{languageRow.course?.courseId || '-'}
                                    <br />
                                    LTI #{languageRow.course?.ltiActivityId || '-'}
                                  </TableCell>
                                  <TableCell>
                                    <CourseIdentity course={languageRow.course} />
                                  </TableCell>
                                  <TableCell>
                                    {(languageRow.issues || []).length
                                      ? languageRow.issues.join(', ').replace(/_/g, ' ')
                                      : 'Ready'}
                                  </TableCell>
                                  <TableCell>
                                    {languageRow.lastValidatedAt
                                      ? new Date(languageRow.lastValidatedAt).toLocaleString()
                                      : '-'}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Tooltip
                                      title={
                                        getMoodleUrl(languageRow.course)
                                          ? 'Open Moodle course'
                                          : 'Moodle URL is not configured'
                                      }
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={!getMoodleUrl(languageRow.course)}
                                          onClick={() => openMoodle(languageRow.course)}
                                        >
                                          <Icon>open_in_new</Icon>
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    {!languageRow.linkId && languageRow.safe && (
                                      <Button size="small" color="primary" onClick={() => attach(row, languageRow)}>
                                        Attach
                                      </Button>
                                    )}
                                    {languageRow.linkId && languageRow.replacementCandidates?.length > 0 && (
                                      <Button size="small" color="primary" onClick={() => replace(row, languageRow)}>
                                        Replace
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {!row.currentVersion?.languages?.length && (
                                <TableRow>
                                  <TableCell colSpan={8}>No current Moodle language association.</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>

                          <Box mt={2}>
                            <Button
                              startIcon={<Icon>{expandedVersions[row.id] ? 'expand_less' : 'expand_more'}</Icon>}
                              onClick={() =>
                                setExpandedVersions(previous => ({ ...previous, [row.id]: !previous[row.id] }))
                              }
                            >
                              Other versions ({row.otherVersions.length})
                            </Button>
                            <Collapse in={Boolean(expandedVersions[row.id])}>
                              {row.otherVersions.map(version => (
                                <Box key={version.key} mt={1} p={1} border={1} borderColor="grey.300">
                                  <Typography variant="subtitle1">
                                    Version {version.label} — {version.healthy ? 'Ready' : 'Has validation issues'}
                                  </Typography>
                                  {version.courses.map(course => (
                                    <Box key={course.courseId} display="flex" alignItems="center">
                                      <Chip className={classes.chip} size="small" label={course.language} />
                                      <Typography variant="body2">
                                        {course.courseName} — {course.shortname} (#{course.courseId})
                                      </Typography>
                                      {getMoodleUrl(course) && (
                                        <IconButton size="small" onClick={() => openMoodle(course)}>
                                          <Icon>open_in_new</Icon>
                                        </IconButton>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              ))}
                              {!row.otherVersions.length && (
                                <Typography variant="body2">No other versions discovered.</Typography>
                              )}
                            </Collapse>
                          </Box>
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {!inventory.items.length && (
                <TableRow>
                  <TableCell colSpan={11}>No system training parts match the current filters.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={inventory.totalCount}
            page={query.page}
            rowsPerPage={query.pageSize}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onChangePage={(event, page) => updateQuery({ page })}
            onChangeRowsPerPage={event => updateQuery({ pageSize: Number(event.target.value), page: 0 })}
          />
        </Paper>
      </ModalBusyContainer>
    </PageLayout>
  );
};

export default LanguageManager;
